import {formatUrl, getQueryValue} from '@teemill/common/helpers';
import {http, useGtm, snackbar} from '@teemill/common/services';
import {ApiError} from '@teemill/common/errors';
import {captureException} from '@sentry/vue';

export default {
  data() {
    return {
      couponCode: '',
      couponMessage: '',
      waitingForCoupon: false,
      items: [],
      summary: {},
      requiresShipping: true,
      isFreepost: false,
      freepostDisabled: true,
      country: null,
      messages: {},
      stockData: {},
      maxDispatchDays: null,
      stockConfigLoading: false,
      freeShippingThresholdUK: 50,
      freeShippingThresholdEU: null,
    };
  },

  computed: {
    usesNewProductFeed() {
      return this.$store.state.subdomain.usesNewProductFeed;
    },

    coupon: {
      get() {
        return this.couponCode;
      },

      set(value) {
        this.couponCode = value;
      },
    },

    hasMessages() {
      return Object.values(this.messages).length !== 0;
    },
  },

  methods: {
    sendInitiateCheckoutEvent() {
      if (!this.app.isTeemill) {
        useGtm().initiateCheckout({
          contentType: 'product_group',
          items: this.items.map(item => ({
            id: this.usesNewProductFeed ? item.productUuid : item.productId,
            name: item.name,
            brand: JSON.stringify(this.$store.state.subdomain.company.name),
            price: item.priceIncTax,
            quantity: item.quantity,
            currency: this.$store?.state?.subdomain?.currency ?? 'GBP',
            options: item.options.map(option => ({
              color: option.Colour,
              size: option.Size,
            })),
          })),
          value: this.summary.subtotal,
          currency: this.$store?.state?.subdomain?.currency ?? 'GBP',
          numItems: this.$store.getters['cart/getQuantity'],
          userId: this.$store.state.subdomain.sessionId,
        });
      }
    },

    async getBasket() {
      this.waitingForItems = true;

      const cartHash = getQueryValue(window.location.search, 'br');
      const couponCode = getQueryValue(window.location.search, 'cc');

      if (cartHash) {
        // remove query param
        this.$router.push(this.$route.path);

        await http
          .post(formatUrl(`/omnis/v3/basket/${this.name}/recover/`), {
            br: cartHash,
          })
          .success(data => {
            if (data) {
              this.handleCartResource(data);
            }

            this.waitingForItems = false;
          })
          .catch(() => {
            this.handleFatalError();
            this.waitingForItems = false;
          });
      } else {
        await http
          .get(formatUrl(`/omnis/v3/basket/${this.name}/list/`))
          .success(data => {
            if (data) {
              this.handleCartResource(data);
            }

            this.waitingForItems = false;
          })
          .catch(() => {
            this.handleFatalError();
            this.waitingForItems = false;
          });
      }

      if (couponCode) {
        await new Promise((resolve, reject) => {
          this.applyCoupon(
            couponCode,
            message => {
              if (message && message === 'Your coupon code has been applied.') {
                snackbar(message);
              } else if (message) {
                // if unsuccessful, make it clear to customer error is about coupon
                snackbar.error('Coupon: ' + message);
              }

              resolve();
            },
            () => {
              snackbar.error(
                'Sorry, something went wrong, please refresh the page or try again later.'
              );
              reject();
            },
            'cart'
          );
        });
      }

      this.$emit('items-loaded', this.items);
    },

    handleFatalError() {
      snackbar.error(
        'Sorry, something went wrong, please refresh the page or try again later.'
      );
    },

    applyCoupon(coupon, resolve, reject, context) {
      this.waitingForCoupon = true;
      http
        .post(formatUrl('/omnis/v3/basket/apply-coupon/'), {
          coupon,
          name: this.name,
          context,
        })
        .then(({data}) => {
          if (data) {
            if (context === 'checkout') {
              this.handleCheckoutResource(data);
            } else {
              this.handleCartResource(data);
            }
          }

          resolve(data.coupon.message);
        })
        .catch(error => {
          if (error.response) {
            switch (error.response.status) {
              case 422:
                resolve(error.response.data.errors.coupon[0]);
                break;

              default:
                resolve(
                  'Sorry, something went wrong, please refresh the page or try again later.'
                );
            }
          }

          reject();
        });
    },

    handleCartResource(data) {
      this.setItemsData(data.items);
      this.setCouponData(data.coupon);
      this.setMessageData(data.messages);
      this.summary = data.summary;
      this.country = data.country;
      this.requiresShipping = data.requiresShipping;
      this.maxDispatchDays = data.maxDispatchDays ?? null;
      this.isFreepost = data.isFreepost;
      this.freepostDisabled = data.freepostDisabled;
      this.freeShippingThresholdUK = data.freeShippingThresholds.uk;
      this.freeShippingThresholdEU = data.freeShippingThresholds.eu ?? null;
      this.fetchStockConfig();
    },

    async fetchStockConfig() {
      if (
        !this.items ||
        this.items.length === 0 ||
        !this.$store.state.subdomain?.division
      ) {
        return;
      }

      this.stockConfigLoading = true;

      try {
        const params = new URLSearchParams();
        this.items.forEach(item => {
          if (item.options[0]?.productId) {
            item.options.forEach(option => {
              params.append('productIds[]', option.productId);
            });

            return;
          }

          params.append('productIds[]', item.productId);
        });

        const response = await fetch(
          formatUrl(
            `/omnis/v3/division/${
              this.$store.state.subdomain.division
            }/product-stock-config/?${params.toString()}`
          ),
          {
            credentials: 'include',
            mode: 'no-cors',
          }
        ).then(response => {
          if (response.ok) {
            return response.json();
          }

          throw new ApiError();
        });

        this.stockData = response;
      } catch (error) {
        captureException(error);
      } finally {
        this.stockConfigLoading = false;
      }
    },

    setItemsData(items) {
      this.items = items;
      this.$store.commit('cart/setItems', items);
    },

    setCouponData(coupon) {
      this.coupon = coupon.code;
      this.couponMessage = coupon.message;
    },

    setMessageData(messages) {
      this.messages = Object.assign(
        this.messages,
        this.getUnseenMessages(messages)
      );

      this.markMessagesAsSeen();
    },

    clearMessages() {
      this.messages = {};
    },

    markMessagesAsSeen() {
      const keys = Object.keys(this.getUnseenMessages(this.messages));

      if (!keys.length) {
        return;
      }

      keys.forEach(key => {
        this.messages[key].seen = true;
      });

      http.post(formatUrl('/omnis/v3/basket/mark-messages-as-seen/'), {
        name: this.name,
        keys,
      });
    },

    getUnseenMessages(messages) {
      const unseenMessages = {};

      Object.keys(messages).forEach(key => {
        const message = messages[key];

        if (!message.seen) {
          unseenMessages[key] = message;
        }
      });

      return unseenMessages;
    },

    getItemDispatchDays(item, selectedColour, selectedSize) {
      if (typeof item.dispatchDays === 'number') {
        return item.dispatchDays;
      }

      if (item.options?.[0]?.productId) {
        return Math.max(
          ...item.options.map(option =>
            this.getItemDispatchDays(option, option['Colour'], option['Size'])
          )
        );
      }

      if (!selectedColour || !selectedSize || !this.stockData) {
        return 0;
      }

      const stockData =
        this.stockData[item.productId]?.[selectedColour]?.sizes?.[selectedSize];

      if (!stockData) {
        return 0;
      }

      return stockData?.dispatchDays ?? 0;
    },

    getMaxDispatchDays() {
      return this.maxDispatchDays ?? 0;
    },
  },
};
